// --- Dependencies -------------------------------------------------------------------------------
import type { ReactNode } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import Icon from "@/UI/atoms/Icon";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type Props = {
    title: string;
    /** The one figure the chart is about, spelled out — a chart says which is biggest, never how many. */
    figure?: string;
    /** A sentence under the marks: what the figure is out of, or where the numbers came from. */
    note?: ReactNode;
    /** The screen this chart is a summary of. The title becomes the way in; without it, it is a heading. */
    onOpen?: () => void;
    children: ReactNode;
};

/**
 * The frame every chart on Home sits in. It exists so six charts cannot end up in six slightly
 * different cards, and so the exact number lives beside the shape rather than only in a tooltip
 * nobody on a phone can open.
 *
 * The **title** is the way into the section, not the card: a whole card that navigates would swallow
 * every hover and click the chart itself needs for its tooltips.
 */
export default function ChartCard({ title, figure, note, onOpen, children }: Props) {
    return (
        <article className="chart-card">
            <header className="chart-card__head">
                {onOpen ? (
                    <button type="button" className="chart-card__title chart-card__title--opens" onClick={onOpen}>
                        <span>{title}</span>
                        <Icon name="chevron" size={14} />
                    </button>
                ) : (
                    <h2 className="chart-card__title">{title}</h2>
                )}

                {figure && <strong className="chart-card__figure">{figure}</strong>}
            </header>

            <div className="chart-card__plot">{children}</div>

            {note && <p className="chart-card__note">{note}</p>}
        </article>
    );
}
